import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, exhaustMap, from, tap, catchError } from 'rxjs';
import { StreamingHubService } from '@pcp/streaming';
import { PCP_LOGGER, PcpLoggerInterface } from '@pcp/logging';
import { authServiceActions, fromAuth } from '@pcp/auth';
import { ClassroomFacade } from '../../public/yjs/classroom.facade';
import { DisplayStateSnapshot } from '../../public/yjs/lesson-ydoc-schema';
import { resignDisplayStateSnapshotUrls } from '../yjs/resign-display-state-snapshot';
import { startLessonActions } from '../../public/store/lesson.actions';
import * as fromLesson from '../../public/store/lesson.selectors';
import { classroomLifecycleEvents } from '../../public/store/classroom-sync.actions';
import { classroomConnectionActions } from '../../public/state/classroom.actions';

/**
 * Lifecycle effects: tie the Classroom Y.Doc lifecycle to NgRx actions.
 *
 * - `initOnLessonStart$`: Teacher starts a fresh lesson → fresh synced Y.Doc; the
 *   draft's display state crosses the swap via the export/import handoff.
 * - `initOnClassroomConnect$`: Any client connects to the classroom → restore from Redis
 *   (no-op only when the current document already belongs to the joined lesson).
 * - `returnToDraftOnLessonStop$`: Lesson is stopped → the teacher returns to a draft
 *   Y.Doc carrying the stopped lesson's display state. Teacher-scoped by construction:
 *   `lessonHasBeenStopped` never fires on other client types (see
 *   `ClassroomLifecycleEffects`); consumers tear down via `discardOnLogout$` instead.
 * - `discardOnLogout$`: Session ends → the document is destroyed, every client type.
 *   No user ⇒ no lesson state.
 */
@Injectable()
export class LessonYjsLifecycleEffects {
    private readonly actions$ = inject(Actions);
    private readonly facade = inject(ClassroomFacade);
    private readonly streamingHub = inject(StreamingHubService);
    private readonly store = inject(Store);
    private readonly log: PcpLoggerInterface = inject(PCP_LOGGER).createScoped('yjs');

    private readonly sas = this.store.selectSignal(fromAuth.selectSas);

    /**
     * Synchronous session check for the stop/logout interplay. The auth reducer
     * resets on `userHasBeenLoggedOut` BEFORE any effect sees the action, so by
     * the time the logout-driven stop chain reaches `lessonHasBeenStopped`
     * (asynchronously, after its hub/window promises), this is reliably `false`.
     */
    private readonly isAuthenticated = this.store.selectSignal(fromAuth.selectIsAuthenticated);

    /**
     * Re-sign a handoff snapshot's URL-bearing payloads with the current SAS.
     * Supplied from here (not the service/binding — both are deliberately
     * Store-less/SAS-agnostic) because `selectSas` reads `claims.sas` off the auth
     * JWT, so it is always the current token's value: the document ends up with a
     * SAS exactly as fresh as it would have been without a handoff. With no SAS
     * available the snapshot passes through untouched — a carried URL with its
     * original SAS beats one signed with `''`.
     */
    private readonly resignUrls = (snapshot: DisplayStateSnapshot): DisplayStateSnapshot => {
        const sas = this.sas();
        if (!sas) return snapshot;
        return resignDisplayStateSnapshotUrls(snapshot, sas,
            (message, err) => this.log.error(message, err as Error));
    };

    /**
     * Ephemeral (awareness) broadcasts are gated on the lesson actually running.
     *
     * Supplied here rather than read inside `LessonYjsService` for the same reason as
     * `resignUrls`: the service holds no Store. It matters because Y.Doc teardown is bound
     * to **logout**, not lesson stop — a beamer, PtB student or live-lesson client keeps a
     * live provider after the lesson ends, and without this gate would keep broadcasting
     * into a finished lesson.
     *
     * `isLessonStarted` only became meaningful on consumers once
     * `receivedStopLessonCommand` was added to the reducer's stop group; before that every
     * transition clearing `started` came from a teacher-only action, so this gate would
     * have been inert on exactly the clients it exists for.
     */
    private readonly awarenessSendEnabled$ = this.store.select(fromLesson.isLessonStarted);

    readonly initOnLessonStart$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    startLessonActions.lessonStartedAndBeamerWasAutomaticallyOpened,
                    startLessonActions.lessonStarted_connectionInfoModalWasShown,
                ),
                exhaustMap(() => {
                    const connection = this.streamingHub.connection;
                    const lessonId = this.streamingHub.getJoinedLessonId();
                    if (!connection || !lessonId) {
                        this.log.error('initOnLessonStart$: Missing prerequisites', undefined,
                            { hasConnection: !!connection, lessonId });
                        return EMPTY;
                    }
                    return from(this.facade.initialize(lessonId, connection, {
                        isFreshLesson: true,
                        resignUrls: this.resignUrls,
                        awarenessSendEnabled$: this.awarenessSendEnabled$,
                    })).pipe(
                        catchError(err => {
                            this.log.error('initOnLessonStart$: Yjs initialize failed', err as Error, { lessonId });
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    readonly initOnClassroomConnect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(classroomConnectionActions.connectToClassroomSuccess),
                exhaustMap(() => {
                    const connection = this.streamingHub.connection;
                    const lessonId = this.streamingHub.getJoinedLessonId();
                    if (!connection || !lessonId) {
                        this.log.error('initOnClassroomConnect$: Missing prerequisites', undefined,
                            { hasConnection: !!connection, lessonId });
                        return EMPTY;
                    }
                    // Guard on "synced for THIS lesson", not merely "a provider is
                    // attached" (the old isOnline()) or "a document exists"
                    // (isInitialized()): a reconnect to the same lesson must not
                    // re-fetch state the client already has, but a document left
                    // over from a draft or from a DIFFERENT lesson must never
                    // suppress initialization — that is how a stale document used
                    // to be rendered for an entire later lesson.
                    if (this.facade.isSyncedToLesson(lessonId)) return EMPTY;
                    return from(this.facade.initialize(lessonId, connection, {
                        awarenessSendEnabled$: this.awarenessSendEnabled$,
                    })).pipe(
                        catchError(err => {
                            this.log.error('initOnClassroomConnect$: Yjs initialize failed', err as Error, { lessonId });
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Lesson stop → the teacher returns to draft mode on the slide that was live:
     * export the stopped lesson's display state, tear the synced document down, and
     * re-create the local-only draft seeded with that snapshot (the lesson → draft
     * handoff). Lesson-scoped state (status/pause/pending-stop, primary-slide
     * history) dies with the destroyed document, so the draft reports no active
     * lesson and the next lesson starts with a clean history.
     *
     * Teacher-scoped by construction: both dispatchers of `lessonHasBeenStopped`
     * are teacher-only (see `ClassroomLifecycleEffects`), so this effect never
     * fires on a beamer or student. Consumers learn of a stop via the SignalR
     * `STOP_LESSON` command and react locally per app; their document teardown is
     * `discardOnLogout$`.
     *
     * Session gate: the draft is a property of an authenticated teacher. A logout
     * drives this same stop chain (`stopLesson$` listens to `userHasBeenLoggedOut`,
     * and the chain is best-effort all the way down, so it completes even against
     * a stopped connection — observed 2026-08-07). Without the gate, the re-init
     * below would land AFTER `discardOnLogout$` and re-seed a draft with the
     * outgoing account's display state — the exact leak this rule exists to
     * prevent. With no session, the end state must be *no document*.
     */
    readonly returnToDraftOnLessonStop$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(classroomLifecycleEvents.lessonHasBeenStopped),
                tap(() => {
                    if (!this.isAuthenticated()) {
                        // No session — discard instead of returning to draft
                        // (idempotent when discardOnLogout$ already ran).
                        this.facade.destroy();
                        return;
                    }

                    // Export before destroy — initializeOffline() destroys internally,
                    // so the snapshot must be captured at the call site.
                    let snapshot: DisplayStateSnapshot | null = null;
                    try {
                        snapshot = this.facade.exportDisplayState();
                    } catch (err) {
                        this.log.error('returnToDraftOnLessonStop$: export failed — returning to an empty draft', err as Error);
                    }

                    this.facade.destroy();

                    try {
                        this.facade.initializeOffline(snapshot ?? undefined, this.resignUrls);
                    } catch (err) {
                        // Fall through to the destroyed state (pre-change behaviour):
                        // a failed re-init must not leave this effect stream dead.
                        this.log.error('returnToDraftOnLessonStop$: draft re-init failed — Y.Doc stays destroyed', err as Error);
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Session end → discard the document. Every client type, no gate.
     *
     * Logout is the teardown boundary, not lesson stop: lesson state is
     * meaningless without a session, and logout strictly subsumes the stop case
     * on consumers (a beamer's stop chain *causes* its logout) while also
     * covering the paths a stop never sees — a rejected token refresh
     * (`logoutAndClearTokensOnRefreshFailure$` dispatches this action without
     * calling `logOut()`), a logout broadcast from another tab, a manual logout.
     *
     * The teacher is deliberately NOT exempt: its draft is the worst case, not a
     * keepsake — `initialize({ isFreshLesson: true })` imports the surviving
     * draft into the next lesson's fresh document, so a draft that outlives the
     * session is written into the next account's lesson and broadcast to its
     * classroom. Consumer state is fully server-restorable on the next join.
     *
     * Safe when there is nothing to tear down: `destroy()` is null-guarded and
     * idempotent, and `provider.disconnect()` only detaches local handlers, so
     * it is a no-op against a connection the stop chain already stopped.
     */
    readonly discardOnLogout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authServiceActions.userHasBeenLoggedOut),
                tap(() => {
                    this.log.info('discardOnLogout$: session ended — discarding lesson state',
                        { hadDocument: this.facade.isInitialized() });
                    this.facade.destroy();
                }),
            ),
        { dispatch: false },
    );
}
